import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { loadMedicalBoardOptions } from '~/api/utils';
import { AsyncCreatableSelect, FormRow as Row, FormSection as Section, Select } from '~/components';
import { OptionWithIdType } from '~/features/types';
import { useCustomForm } from '~/hooks/useCustomForm';
import { EMPTY_OPTION, EMPTY_OPTION_WITH_ID, TRAINING_TYPES } from '../../constants';
import { TypeAndBoardSchema } from '../../schemas';
import { TrainingFormValues } from '../../types';
import { TypeAndBoardFormValues } from './types';
import { WizardFooter } from './WizardFooter';

const TAGS = CT.academics.training.wizard;

interface Props {
  currentPage: number;
  form: Partial<TrainingFormValues>;
  providerCategoryId?: number;
  providerId: number;
  wizardNext: (values: Partial<TrainingFormValues>) => void;
  wizardPrev: (values?: Partial<TrainingFormValues>) => void;
  wizardSubmit: (values: Omit<TrainingFormValues, 'id'>) => void;
  closeWizard: () => void;
  setSpecBoardOptions: (values: OptionWithIdType[]) => void;
}

export const TypeAndBoard = (props: Props) => {
  const { form, providerCategoryId, providerId, setSpecBoardOptions } = props;

  const methods = useCustomForm<TypeAndBoardFormValues>({
    resolver: zodResolver(TypeAndBoardSchema),
    defaultValues: {
      trainingType: form.trainingType,
      medicalBoard: form.medicalBoard,
    },
  });
  const { watch, setValue } = methods;

  const medBoard = watch('medicalBoard');
  useEffect(() => {
    setSpecBoardOptions([]);
  }, [medBoard, setSpecBoardOptions]);

  useEffect(() => {
    const setMedicalBoard = async () => {
      if (!providerCategoryId) return;
      if (providerId) return;

      const options = await loadMedicalBoardOptions(providerId, providerCategoryId);
      if (!form.medicalBoard) return;
      const medicalBoard = options.find((option) => option.value === form.medicalBoard?.value);
      if (!medicalBoard) return;
      setValue('medicalBoard', medicalBoard);
    };
    setMedicalBoard();
  }, []);

  const handleMedicalBoardChange = (medicalBoard: { label: string; value: string }) => {
    form.medicalBoard = medicalBoard;
    form.specialtyBoard = EMPTY_OPTION_WITH_ID;
    form.specialtyCertification = EMPTY_OPTION_WITH_ID;
    form.subSpecialtyCertification = EMPTY_OPTION;
  };

  const handleMedicalBoardCreate = (inputValue: string) => {
    const medicalBoard = { label: inputValue, value: 'userAdded' };
    setValue('medicalBoard', medicalBoard);
    handleMedicalBoardChange(medicalBoard);
  };

  return (
    <FormProvider {...methods}>
      <Section componentsPerLine={1}>
        <Row>
          <Select
            label={'Training Type'}
            name={'trainingType'}
            cyTag={TAGS.trainingTypeSelect}
            options={TRAINING_TYPES}
            isSearchable={false}
          />
        </Row>
        <Row>
          <AsyncCreatableSelect
            name={'medicalBoard'}
            label={'Medical Board*'}
            cyTag={TAGS.medicalBoardSelect}
            onCreateOption={handleMedicalBoardCreate}
            onChange={(option: SingleValue<{ value: string; label: string }>) => {
              if (option) {
                // If user goes back to the form and changes the board, other form fields reset.
                handleMedicalBoardChange(option);
              }
            }}
            minChars={false}
            loadOptions={() =>
              providerCategoryId && providerId
                ? loadMedicalBoardOptions(providerId, providerCategoryId)
                : Promise.resolve([])
            }
          />
        </Row>
        <WizardFooter {...props} />
      </Section>
    </FormProvider>
  );
};
